/**
 * Внешнее плечо аварийного маршрута (запрос владельца 27.07): когда наших
 * дорожек к укрытию нет, строим путь по городским дорогам от текущей позиции
 * пользователя (внешний навигатор /api/route-ext), как обычный маршрут. Прямой
 * пунктир остаётся крайним фолбэком (нет гео/провайдера). Только режим «Укрытие».
 */

import type { Venue } from '@app/rvf'
import { useEffect, useState } from 'preact/hooks'
import { nearestShelterPoint } from '../emergency/escape-route'
import type { EmergencyFrame } from '../emergency/frame'
import { ensureGeoStart } from '../emergency/locate'
import { type ExternalLeg, fetchExternalLeg } from '../route-splice'

/**
 * @param hasPlan — наша сеть уже довела до укрытия (внешнее плечо не нужно).
 * @returns городское плечо `от меня → ближайшее укрытие` или null.
 */
export function useExternalEscape(opts: {
  v: Venue | null
  frame: EmergencyFrame | null
  hasPlan: boolean
  shelterId: string | null
}): ExternalLeg | null {
  const { v, frame, hasPlan, shelterId } = opts
  const [external, setExternal] = useState<ExternalLeg | null>(null)
  useEffect(() => {
    setExternal(null)
    if (!v || !frame || frame.mode !== 1 || hasPlan) return
    if (!(v.evacuation?.shelters?.length ?? 0)) return
    let cancelled = false
    void ensureGeoStart().then((geo) => {
      if (cancelled || !geo) return
      const shelterPt = nearestShelterPoint(v, geo, shelterId)
      if (!shelterPt) return
      return fetchExternalLeg(geo, shelterPt).then((leg) => {
        if (!cancelled && leg && leg.polyline.length >= 2) setExternal(leg)
      })
    })
    return () => {
      cancelled = true
    }
  }, [v, frame, hasPlan, shelterId])
  return external
}
